import { z } from 'zod';
import {
  businessSchema,
  createModel,
  imageGoogleMapSchema,
  PartialData,
  QueryMany,
  refSchema,
  tenantSchema,
} from '../../common';
import { orderStatusSchema } from './order.status.model';
import { orderDatesSchema } from './order.dates.model';
import { orderLoadSchema } from './order.load.model';
import { orderDestinationSchema } from './order.destination.model';
import { orderCarrierSchema } from './order.carrier.model';
import { orderDriverSchema } from './order.driver.model';
import { orderVehicleSchema } from './order.vehicle.model';
import { orderDistanceSchema } from './order.distance.model';
import { orderTimelineSchema } from './order.timeline.model';
import { orderSettingsSchema } from './order.settings.model';
import { orderImageSchema } from './order.image.model';
import { orderAnalyticsSchema } from './order.analytics.model';
import { orderBeneficiarySchema, orderShipperSchema } from './order.beneficiary.model';
import { orderProblemSchema } from './order.problem.model';
import { orderAlertSchema } from './order.alert.model';
import { orderWaybillBayanSchema, orderWaybillSchema } from './order.waybill.model';
import { UTagSharedSchema } from '../utag';
import { orderCancellationSchema } from './order.cancellation.model';
import { orderPODOwnerSchema } from './order.pod.model';
import { orderOwnerSchema } from './order.owner.model';
import { orderCommentUpdateSchema } from './order.update.model';
import { orderReviewSchema } from './order.review.model';
import { orderContractElementSchema } from './order.contract.model';
import { orderAllocationSchema } from './order.allocation.model';
import { orderSubscriptionCaptureSchema } from './order.subscription.model';
import { partySchema } from '../../common';

/** `order db model` */
export const OrderSchema = createModel({
  status: orderStatusSchema,

  /**
   * Which business this order belongs to (Hero vs Wathiqa Plus).
   * Stamped at create from app/persona; used for waybill gating.
   * Optional only for legacy rows — new writes always set it.
   */
  business: businessSchema.optional(),

  /** the owner of the POD currently. This can change multiple times throughout the order journey.
   *
   * For example, an admin user can manage receiving pods from carriers,
   *  and then another person later on can managing sending pods to customers.
   *
   * However, one person at a time can manage an order POD.
   *
   * All the history of the POD changes will be found in the order timeline.
   */
  podOwner: orderPODOwnerSchema.optional(),

  /** POD reference number. This is a custom reference specific to POD documents.
   *
   * Can be set manually by admin users to help with order lookup and customer inquiries.
   * Similar to customRef in images, but applies to the entire POD for the order.
   */
  podRef: z.coerce.string().trim().optional(),

  /**
   * Person responsible for handling this order — who the team and Hero AI
   * go back to for updates, questions, and messaging (including waybill).
   *
   * Defaults to the creating user (with phone snapshot). Reassignable in the
   * dashboard to another existing user. One owner at a time; history lives on
   * the order timeline when ownership changes are recorded.
   */
  orderOwner: orderOwnerSchema.optional(),

  /** order ref */
  ref: refSchema,

  /** the pickup / drop off dates  */
  dates: orderDatesSchema,

  /** the order load info such as items, costs, job etc */
  load: orderLoadSchema,

  /**
   * the destinations the truck will go to.
   *
   * the destinations array must have 2 elements (pickup & dropoff)
   * */
  destinations: z.array(orderDestinationSchema).min(2),

  /**
   * The **shipper** — the Party who published this order. Network-neutral
   * by design: the shipper can be a business customer publishing for
   * themselves, a carrier publishing on behalf of a client, a broker
   * publishing for resale, or any Org publishing an internal move.
   *
   * `Order.shipper` is the canonical originator across all surfaces:
   *
   * - It is the authority for the public-promotion rule (only the
   *   shipper may broadcast an Offer with `visibility='public_load_board'`).
   * - It is the seed of `Order.tenants[]` (`tenants.builder.ts:recompute`
   *   walks `shipper.ids[]` first).
   * - The lens query (`as_shipper`) keys off this field directly.
   *
   * The shipper is NOT necessarily the buyer of every hop. The bilateral
   * commercial chain lives in `Order.contracts[]`, and per-hop pricing /
   * payment lives in `contracts[i].amount` ([amount.schema.ts](libs/shared/schema/src/common/amount.schema.ts)
   * already carries `paid` and `remaining`). Internal-vs-on-behalf is
   * derived by comparing `shipper.ids[]` with `client.ids[]` — see
   * `apps/backend/src/app/order/utils/order.shipper.ts`.
   *
   * Phase 7 rename — was `Order.user` before; renamed to remove the
   * implicit "I'm the seller" assumption. No DB migration; new writes
   * use the `shipper` field name only.
   **/
  shipper: orderShipperSchema,

  /**
   * Persisted Party snapshot of the buyer Client this order is being
   * created **for** (a `target: 'client'` Party with `ids[]` carrying
   * the `client_id` and any linked `org_id` / `user_id`).
   *
   * Set at create time when `clientId` is present on the API
   * payload — see `apps/backend/src/app/order/create/order.builder.ts`
   * (Resolver populates from `Client` + `Client.linkedParty`). Stays
   * undefined for walk-in / internal-shipment flows.
   *
   * Avoids re-deriving the Party for every Order read; tenants[] is
   * still recomputed alongside this on every write.
   */
  client: partySchema.optional(),

  /** Dropoff-side beneficiary — who signs for the goods at destination.
   *
   * **Interim:** mirrors the CRM buyer Party when a Client is picked —
   * same snapshot as `sender` and `Order.client`. Unset for internal
   * shipments. **Future:** may be a third party (client's customer at
   * dropoff) while `sender` stays at the pickup site.
   */
  receiver: orderBeneficiarySchema.optional(),
  /** Pickup-side beneficiary — who hands the goods over at origin.
   *
   * **Interim (until custom sender/receiver UI ships):** when a CRM
   * Client is picked at publish time, `sender` mirrors the buyer Party
   * (`Order.client`) — same snapshot as `receiver`. Internal shipments
   * leave both unset.
   *
   * **Future:** a carrier publishing on behalf of a client will be able
   * to set sender to a third party (e.g. the client's customer at the
   * pickup site) while `receiver` may be the client, the client's
   * customer at dropoff, or the same party for warehouse moves.
   */
  sender: orderBeneficiarySchema.optional(),

  /**
   * The executing carrier of the load — the carrier org whose driver is
   * (or will be) assigned to physically move the order. Set by the
   * allocation flow once a carrier has been picked up (own-fleet path:
   * the creator's own Org; targeted/private-network: the accepting
   * carrier from the Offer). Stays `undefined` while the order is in
   * `searching_for_driver` with no accepted Offer yet.
   *
   * Distinct from the commercial chain (which lives on
   * {@link contracts}). A broker chain `C → B → X` puts X in
   * `Order.carrier` (the executor), even though B sits on
   * `contracts[1]` between them.
   */
  carrier: orderCarrierSchema.optional(),

  /**
   * the embedded bilateral commercial chain. ONE element per hop; chain order
   * is recovered dynamically from `downstream.buyer._id === upstream.seller._id`
   * matching (see `OrderService.buildContractChain`).
   *
   * The **executing** carrier of the load is recovered as `contracts[last].seller`
   * (or `Order.driver.org` for own-fleet dispatch \u2014 see `Order.allocation`).
   * The transitional `Order.carrier` field above is NOT consulted by new
   * code.
   *
   * ACL: filtered by `OrderService.projectOrderForViewer` to legs where the
   * viewer is `buyer._id` or `seller._id`. The Order itself is gated upstream
   * by `Order.tenants[]` (accepted relationships only).
   *
   * See [order.contract.model.ts](libs/shared/schema/src/model/order/order.contract.model.ts).
   */
  contracts: z.array(orderContractElementSchema).default([]),

  /**
   * `Order.user`'s policy for sourcing a carrier. Set at creation time;
   * intermediaries do NOT mutate this (their downstream dispatch is governed
   * by their own `Offer.visibility` choice, subject to the public-promotion
   * rule). See [order.allocation.model.ts](libs/shared/schema/src/model/order/order.allocation.model.ts).
   */
  allocation: orderAllocationSchema,

  /**
   * auto-computed visibility set: every party who can legitimately see this
   * Order in their "my orders" list. Built from ACCEPTED relationships only:
   *
   *   - `Order.shipper` (the publisher)
   *   - `Order.client` (CRM-tagged counterparty, when set)
   *   - every `contracts[i].buyer` + `contracts[i].seller`
   *     (accepted / completed / cancelled all retain tenant membership for history)
   *   - `Order.driver._id` + `Order.driver.org._id`
   *   - `sender.customer._id` + `sender.contact.phone` + `sender.contact.userId`
   *   - `receiver.customer._id` + `receiver.contact.phone` + `receiver.contact.userId`
   *
   * Open-offer targets are EXPLICITLY EXCLUDED \u2014 they reach the load via the
   * Offer collection (`Offer.loadSummary` inbox + privileged `getOrderPreviewForTarget`)
   * until they accept. Recomputed by `TenantsBuilder.recompute` in the same
   * write whenever `contracts[]` / `driver` / `sender` / `receiver` / `client` changes.
   *
   * Indexed `{ 'tenants.value': 1, 'tenants.type': 1 }` so any "orders where
   * my org/user/phone is a tenant" is a single index seek.
   */
  tenants: z.array(tenantSchema).default([]),

  /**
   * the driver assigned to the order.
   *
   * if this is empty, then it means that no driver has been assigned to this order yet
   */
  driver: orderDriverSchema.optional(),
  /**
   * the actual vehicle assigned for the order.
   *
   * if this is empty, then it means that the driver has not been assigned to this order, and therefore there is no vehicle yet
   */
  vehicle: orderVehicleSchema.optional(),

  /** the distance & duration for all destinations */
  distance: orderDistanceSchema,

  /** a timeline of order events such as (created / driver assigned / done / etc) */
  timeline: orderTimelineSchema,

  /** this will be an image of the map (Google map static API).
   *
   * this Image will not be saved in DB ... it will be returned on the app level
   */
  routeImage: imageGoogleMapSchema.optional(),

  /** order images such as proof of delivery, place, or any picture that will be taken during the order journey */
  images: z.array(orderImageSchema).default([]),

  /** the cancellation info of the order. This must be filled when the order is canceled. */
  cancellation: orderCancellationSchema.optional(),

  /** the review info of the order. This is filled when an admin verifies a delivered order.
   *
   * a review is to indicate that all order info is verfied such as
   * - Prices & Fees (customer & carrier)
   * - Accurate Locations (pickup & dropoff)
   * - Customer & Receiver info (name, contact, address, etc)
   * - ... etc
   */
  review: orderReviewSchema.optional(),

  /** order setting such as images */
  settings: orderSettingsSchema,

  /** optional instructions by the user such as (don't forget to do XYZ) */
  instructions: z.string().optional(),

  /** Overall order analytics for payment, destinations, etc */
  analytics: orderAnalyticsSchema,

  /** report any problem with the order such as accident, theft, item damages, etc */
  problem: orderProblemSchema.optional(),
  /** alert message to be displayed to the user (carrier or user).
   *
   * **IMPORTANT** to note that this field will not be saved in the `db`. Instead, it will be generated depending on several factors such as order status, destination, etc.
   */
  alert: orderAlertSchema.optional(),
  /** the order waybill as a PDF.
   *
   * It's optional because the waybill will be generated only after the following happens:
   *
   * - the order has been confirmed
   * - The driver has been assigned
   */
  waybill: orderWaybillSchema.optional(),

  /**
   * The GOVERNMENT waybill (وثيقة النقل) issued through Bayan / Logisti.
   *
   * A separate artifact from `waybill` above, which is Hero's own branded PDF.
   * This one has legal standing, carries a Bayan trip id a TGA inspector can
   * check, and only exists when an Org party to the order has the Logisti app
   * installed. See {@link orderWaybillBayanSchema}.
   */
  waybillBayan: orderWaybillBayanSchema.optional(),

  /**
   * Which subscription credits this order consumed, one entry per
   * paying org — see {@link orderSubscriptionCaptureSchema}.
   *
   * The authoritative record of consumption for prepaid (`usage`)
   * subscriptions; `Subscription.balance` is the cache of it. Empty on
   * every order whose parties are all on period plans, which meter by
   * counting orders in a window instead.
   */
  subscriptions: z.array(orderSubscriptionCaptureSchema).default([]),

  /** if true, then it means the driver must pay before the driver unloads the items */
  userMustPayBeforeUnloadingItems: z.boolean().optional(),

  /** the updates to the order such as comments, images, etc @TBC --- this will be integrated later */
  updates: z.array(orderCommentUpdateSchema),

  UTags: z.array(UTagSharedSchema),
});

/** `order db model` */
export type Order = z.infer<typeof OrderSchema.model>;
export type OrderData = z.infer<typeof OrderSchema.data>;
export type OrderPartial = PartialData<Order>;
export type OrderQuery = QueryMany<Order>;
